import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './ExerciseVideoDemo.css';

// ──────────────────────────────────────────────────────────────────────────────
// Exercise Video Demo
//
// Isolated playground for iterating on a 9:16 portrait video layout. NOT wired
// into production. Uses the image-to-video (reference-image) demo clips. Knobs:
//   - exercise : flip between the generated sample clips
//   - layout   : immersive (info overlaid) vs card (info stacked below)
//   - mute     : Veo clips include audio; muted by default
// ──────────────────────────────────────────────────────────────────────────────

const GCS = 'https://storage.googleapis.com/mosaime-workout-media/exercises';

type LayoutMode = 'immersive' | 'card';

interface DemoExercise {
  slug: string;          // GCS folder
  name: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  muscles: string[];
  tips: string[];
  sets?: number;
  reps?: number;
  hold?: number;         // seconds — for held poses (yoga) instead of reps
  rest?: number;
  clip?: 'demo-img' | 'demo';  // image-to-video (default) vs text-to-video file
}

// Each clip lives at exercises/<slug>/demo-img.mp4 (image-to-video output).
const EXERCISES: DemoExercise[] = [
  {
    slug: 'push-up', name: 'Push-up', difficulty: 'Beginner',
    sets: 3, reps: 12, rest: 60,
    muscles: ['Chest', 'Triceps', 'Front Deltoid', 'Core'],
    tips: [
      'Keep a straight line from head to heels',
      'Elbows track back at ~45°, not flared',
      'Lower until your chest is just above the floor',
      'Brace your core — no sagging hips',
    ],
  },
  {
    slug: 'jumping-jack', name: 'Jumping Jack', difficulty: 'Beginner',
    sets: 3, reps: 30, rest: 30,
    muscles: ['Full Body', 'Calves', 'Shoulders'],
    tips: [
      'Land softly through the balls of your feet',
      'Fully extend arms overhead each rep',
      'Keep a steady, bouncy rhythm',
      'Stay light on your feet',
    ],
  },
  {
    slug: 'bicycle-crunch', name: 'Bicycle Crunch', difficulty: 'Intermediate',
    sets: 3, reps: 20, rest: 45,
    muscles: ['Abs', 'Obliques', 'Hip Flexors'],
    tips: [
      'Rotate from the ribs, not the elbows',
      'Keep your lower back pressed to the floor',
      'Extend each leg fully before switching',
      'Move slow and controlled, not rushed',
    ],
  },
  {
    slug: 'warrior-ii', name: 'Warrior II', difficulty: 'Beginner',
    hold: 30, rest: 10,
    muscles: ['Quadriceps', 'Glutes', 'Adductors', 'Shoulders'],
    tips: [
      'Front knee tracks over the second toe',
      'Sink the hips low and steady',
      'Reach actively through both arms',
      'Relax the shoulders down',
    ],
  },
  {
    slug: 'bicep-curl', name: 'Bicep Curl', difficulty: 'Beginner',
    sets: 3, reps: 12, rest: 60,
    muscles: ['Biceps', 'Forearms'],
    tips: [
      'Keep your elbows pinned at your sides',
      'Curl with control — no swinging',
      'Squeeze at the top of each rep',
      'Lower slowly through the full range',
    ],
  },
  {
    slug: 'romanian-deadlift', name: 'Romanian Deadlift', difficulty: 'Intermediate',
    sets: 3, reps: 10, rest: 90,
    muscles: ['Hamstrings', 'Glutes', 'Lower Back'],
    tips: [
      'Hinge at the hips, not the waist',
      'Keep the bar close to your legs',
      'Maintain a flat, neutral back',
      'Feel the stretch in your hamstrings',
    ],
  },
  {
    slug: 'crunch', name: 'Crunch', difficulty: 'Beginner',
    sets: 3, reps: 15, rest: 45,
    muscles: ['Abs', 'Hip Flexors'],
    tips: [
      'Curl up from the ribs, not the neck',
      'Keep your lower back on the mat',
      'Exhale as you lift',
      'Lower slowly with control',
    ],
  },
  {
    slug: 'leg-swing', name: 'Leg Swing', difficulty: 'Beginner',
    sets: 2, reps: 12, rest: 20,
    muscles: ['Hip Flexors', 'Glutes', 'Hamstrings'],
    tips: [
      'Hold a wall for balance',
      'Swing from the hip, stay tall',
      'Keep your torso steady',
      'Controlled swing, no forcing the range',
    ],
  },
  {
    slug: 'downward-facing-dog', name: 'Downward-Facing Dog', difficulty: 'Beginner',
    hold: 30, rest: 10,
    muscles: ['Hamstrings', 'Calves', 'Shoulders'],
    tips: [
      'Press the floor away through your hands',
      'Lift your hips up and back',
      'Lengthen your spine',
      'Soften the knees if hamstrings are tight',
    ],
  },
  {
    slug: 'standing-quad-stretch', name: 'Standing Quad Stretch', difficulty: 'Beginner',
    hold: 30, rest: 10,
    muscles: ['Quadriceps', 'Hip Flexors'],
    tips: [
      'Hold a wall if you need balance',
      'Keep your knees close together',
      'Tuck your pelvis for a deeper stretch',
      'Stand tall — no leaning forward',
    ],
  },
];

const DIFFICULTY_COLOR: Record<string, string> = {
  Beginner:     '#4caf87',
  Intermediate: '#f5a623',
  Advanced:     '#e05252',
};

const videoUrl = (ex: DemoExercise) => `${GCS}/${ex.slug}/${ex.clip ?? 'demo-img'}.mp4`;

// ── Portrait video stage ──────────────────────────────────────────────────────

function VideoStage({
  src,
  name,
  muted,
  overlay,
}: {
  src: string;
  name: string;
  muted: boolean;
  overlay?: React.ReactNode;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Re-attempt autoplay whenever the source changes.
  useEffect(() => {
    setLoaded(false);
    const v = ref.current;
    if (v) v.play().catch(() => {});
  }, [src]);

  return (
    <div className="evd-stage">
      <video
        ref={ref}
        key={src}
        className="evd-video"
        src={src}
        muted={muted}
        loop
        autoPlay
        playsInline
        preload="auto"
        onLoadedData={() => setLoaded(true)}
        aria-label={`${name} demonstration`}
      />
      {!loaded && <div className="evd-stage-loading">Loading…</div>}
      {overlay}
    </div>
  );
}

// ── Rotating form cue (one tip at a time, for the immersive overlay) ──────────

function CyclingCue({ tips }: { tips: string[] }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    setI(0);
    if (tips.length <= 1) return;
    const t = setInterval(() => setI(p => (p + 1) % tips.length), 3500);
    return () => clearInterval(t);
  }, [tips]);

  if (tips.length === 0) return null;

  return (
    <div className="evd-cue">
      <span className="evd-cue-label">Form cue</span>
      {/* key forces a remount each change → fade-in animation */}
      <span key={i} className="evd-cue-text">{tips[i]}</span>
    </div>
  );
}

// ── Stat helpers (held poses show hold time; rep work shows sets/reps) ─────────

function statSpans(ex: DemoExercise) {
  if (ex.hold) {
    return [
      { val: `${ex.hold}s`, label: 'Hold' },
      { val: `${ex.rest}s`, label: 'Rest' },
    ];
  }
  return [
    { val: `${ex.sets}`,  label: 'Sets' },
    { val: `${ex.reps}`,  label: 'Reps' },
    { val: `${ex.rest}s`, label: 'Rest' },
  ];
}

function StatRow({ ex }: { ex: DemoExercise }) {
  const stats = statSpans(ex);
  return (
    <div className="evd-stat-row">
      {stats.map((s, i) => (
        <div key={s.label} style={{ display: 'contents' }}>
          {i > 0 && <div className="evd-stat-divider" />}
          <div className="evd-stat">
            <span className="evd-stat-val">{s.val}</span>
            <span className="evd-stat-label">{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Muscles({ ex }: { ex: DemoExercise }) {
  return (
    <div className="evd-muscles">
      {ex.muscles.map(m => (
        <span key={m} className="evd-muscle-chip">{m}</span>
      ))}
    </div>
  );
}

function Tips({ ex }: { ex: DemoExercise }) {
  return (
    <div className="evd-tips">
      <div className="evd-tips-header">Form Tips</div>
      <ul className="evd-tips-list">
        {ex.tips.map((tip, i) => <li key={i}>{tip}</li>)}
      </ul>
    </div>
  );
}

// ── Layout: immersive (info overlaid on the portrait video) ───────────────────

function ImmersiveLayout({ ex, muted }: { ex: DemoExercise; muted: boolean }) {
  return (
    <VideoStage
      src={videoUrl(ex)}
      name={ex.name}
      muted={muted}
      overlay={
        <>
          <div className="evd-overlay-top">
            <div className="evd-progress-pill">Exercise 1 of 6</div>
            <span
              className="evd-difficulty-badge"
              style={{
                background: DIFFICULTY_COLOR[ex.difficulty] + '33',
                color: '#fff',
              }}
            >
              {ex.difficulty}
            </span>
          </div>

          <div className="evd-overlay-bottom">
            <h2 className="evd-overlay-name">{ex.name}</h2>
            <div className="evd-overlay-stats">
              {statSpans(ex).map(s => (
                <span key={s.label}><b>{s.val}</b> {s.label.toLowerCase()}</span>
              ))}
            </div>
            <CyclingCue tips={ex.tips} />
            <div className="evd-overlay-muscles">{ex.muscles.join(' · ')}</div>
          </div>
        </>
      }
    />
  );
}

// ── Layout: card (portrait video hero, info stacked below) ────────────────────

function CardLayout({ ex, muted }: { ex: DemoExercise; muted: boolean }) {
  return (
    <>
      <div className="evd-progress-bar">
        <div className="evd-progress-fill" style={{ width: '16%' }} />
      </div>
      <div className="evd-progress-label">Exercise 1 of 6</div>

      <div className="evd-card-head">
        <h2 className="evd-card-name">{ex.name}</h2>
        <span
          className="evd-difficulty-badge evd-difficulty-badge--solid"
          style={{
            background: DIFFICULTY_COLOR[ex.difficulty] + '22',
            color: DIFFICULTY_COLOR[ex.difficulty],
          }}
        >
          {ex.difficulty}
        </span>
      </div>

      <VideoStage src={videoUrl(ex)} name={ex.name} muted={muted} />

      <StatRow ex={ex} />
      <Muscles ex={ex} />
      <Tips ex={ex} />
    </>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function ExerciseVideoDemo() {
  const [slug, setSlug]     = useState<string>(EXERCISES[0].slug);
  const [layout, setLayout] = useState<LayoutMode>('immersive');
  const [muted, setMuted]   = useState(true);

  const ex = EXERCISES.find(e => e.slug === slug) ?? EXERCISES[0];

  return (
    <div className="evd-page">
      <PublicHeader />

      <main className="evd-root">
        {/* Iteration controls — demo only */}
        <div className="evd-controls">
          <div className="evd-control-group evd-control-group--wide">
            <span className="evd-control-label">Exercise</span>
            <div className="evd-picker">
              {EXERCISES.map(e => (
                <button
                  key={e.slug}
                  className={`evd-pill ${e.slug === slug ? 'evd-pill--active' : ''}`}
                  onClick={() => setSlug(e.slug)}
                >
                  {e.name}
                </button>
              ))}
            </div>
          </div>

          <div className="evd-control-group">
            <span className="evd-control-label">Layout</span>
            <div className="evd-segmented">
              {(['immersive', 'card'] as LayoutMode[]).map(l => (
                <button
                  key={l}
                  className={`evd-seg-btn ${layout === l ? 'evd-seg-btn--active' : ''}`}
                  onClick={() => setLayout(l)}
                >
                  {l === 'card' ? 'Card' : 'Immersive'}
                </button>
              ))}
            </div>
          </div>

          <div className="evd-control-group">
            <span className="evd-control-label">Audio</span>
            <button
              className={`evd-seg-btn evd-seg-btn--toggle ${muted ? '' : 'evd-seg-btn--active'}`}
              onClick={() => setMuted(m => !m)}
            >
              {muted ? '🔇 Muted' : '🔊 Sound on'}
            </button>
          </div>
        </div>

        {/* The phone-shaped portrait frame we iterate inside */}
        <div className={`evd-frame evd-frame--${layout}`} key={`${slug}-${layout}`}>
          {layout === 'immersive'
            ? <ImmersiveLayout ex={ex} muted={muted} />
            : <CardLayout ex={ex} muted={muted} />}

          <button className="evd-btn-primary">Next Exercise</button>
        </div>

        <p className="evd-note">
          Demo only — not wired to production. Iterate the 9:16 layout here, then we
          port the winner into the real exercise screen.
        </p>
      </main>

      <footer className="evd-footer">
        <div className="evd-footer-inner">
          <div className="evd-footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="evd-footer-logo" />
            <span>mosaime</span>
          </div>
          <div className="evd-footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
